import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Separator,
  ButtonToggle,
  ButtonToggleOption,
} from "@showitinc/showit-wc";

import UIActions from "@/actions/UIActions";
import AppConstants from "@/constants/AppConstants";
import DynamicInput from "@/app2components/base/DynamicInput";
import styles from "./ButtonProperties.module.css";
import useStoreState from "@/components/providers/useStoreState";
import AppStore from "@/stores/AppStore";
import { IS_DEVIATION } from "@/constants/DesignConstants.mjs";
import { HOVER } from "@/constants/MergeConstants";

const {
  THEME_TAB_BUTTONS,
  BUTTON_STATE_HOVER,
  BUTTON_STYLE_PRIMARY,
  BUTTON_STYLE_SECONDARY,
  BUTTON_STATE_REGULAR,
} = AppConstants;

/**
 *
 * @param {string} propName
 * @returns
 */
const buttonStateFilter = (propName, buttonState) =>
  buttonState === BUTTON_STATE_HOVER ? propName.startsWith(HOVER) : !propName.startsWith(HOVER);

/**
 * Determines if a prop is an uncustomized inherited property that should be shown in the customize menu
 * @param {Object} prop - The property object to check
 * @param {Array} newCustomProps - Array of newly customized property names
 * @param {Object} customizedParentPropsMap - Map of customized parent properties
 * @returns {boolean} - True if the prop should be shown in the customize menu
 */
const isUncustomizedInheritedProp = (prop, newCustomProps, customizedParentPropsMap) => {
  if (
    !prop.isInherited ||
    newCustomProps.includes(prop.name) ||
    customizedParentPropsMap[prop.name]
  ) {
    return false;
  }

  return true;
};

const ButtonProperties = ({ mappedElement: element, layout }) => {
  const [newCustomProps, setNewCustomProps] = useState([]);
  const buttonState = useStoreState(AppStore, AppStore.getButtonState);
  const containerRef = useRef(null);

  const layoutProps = element
    .get(layout)
    .filter((prop) => buttonStateFilter(prop.name, buttonState));

  const customizedParentPropsMap = {};
  layoutProps.forEach((prop) => {
    const isInheritedParentWithCustomHoverChild =
      prop.children?.length > 0 &&
      prop.isInherited &&
      prop.children.some(
        (child) => element.getIn(["data", layout, ...child.split(".")]) !== undefined,
      );

    if (isInheritedParentWithCustomHoverChild) {
      customizedParentPropsMap[prop.name] = true;
    }
  });

  const customProps = layoutProps.filter(
    (prop) =>
      !prop.isInherited ||
      newCustomProps.includes(prop.name) ||
      customizedParentPropsMap[prop.name],
  );

  const unCustomizedProps = layoutProps.filter((prop) =>
    isUncustomizedInheritedProp(prop, newCustomProps, customizedParentPropsMap),
  );

  const _onAddCustomization = (prop) => setNewCustomProps([...newCustomProps, prop]);
  const _onRemoveCustomization = (prop) =>
    setNewCustomProps(newCustomProps.filter((propName) => propName !== prop));

  const handleAddCustomization = (propName) => {
    _onAddCustomization(propName);

    requestAnimationFrame(() => {
      if (containerRef.current) {
        const event = new CustomEvent("si-scroll-to-bottom", {
          bubbles: true,
          composed: true,
        });
        containerRef.current.dispatchEvent(event);
      }
    });
  };

  const buttonStyleBase = `${element.get("style")}`;
  const buttonStyleDeviation = element.get(IS_DEVIATION, false);
  const onStyleChange = (e) => {
    setNewCustomProps([]);
    element.get("onSetStyle")(e);
  };

  return (
    <div ref={containerRef}>
      <div>
        <div className={styles.buttonStyleToggle}>
          <ButtonToggle
            hideLabel
            fullWidth
            onSIChange={onStyleChange}
            className={styles["button-style-toggle"]}
            disabled={element.get("isLocked")}
          >
            <ButtonToggleOption
              size="lg"
              className={styles["button-style-toggle-option"]}
              checked={buttonStyleBase === BUTTON_STYLE_PRIMARY}
              value={BUTTON_STYLE_PRIMARY}
            >
              Primary
            </ButtonToggleOption>
            <ButtonToggleOption
              size="lg"
              className={styles["button-style-toggle-option"]}
              checked={buttonStyleBase === BUTTON_STYLE_SECONDARY}
              value={BUTTON_STYLE_SECONDARY}
            >
              Secondary
            </ButtonToggleOption>
          </ButtonToggle>
        </div>

        <div style={{ display: "flex", justifyContent: "center" }}>
          <Button
            variant="link"
            className={styles["edit-defaults-button"]}
            onClick={() => UIActions.showTheme(THEME_TAB_BUTTONS)}
          >
            Edit Defaults
          </Button>
        </div>
      </div>

      <div className={styles["state-toggle-container"]}>
        <ButtonToggle
          label="Button State"
          onSIChange={(e) => UIActions.setButtonState(e.detail.value)}
          className={styles["button-state-toggle"]}
        >
          <ButtonToggleOption
            lowercase
            className={styles["button-style-toggle-option"]}
            checked={buttonState === BUTTON_STATE_REGULAR}
            value={BUTTON_STATE_REGULAR}
          >
            Normal
          </ButtonToggleOption>
          <ButtonToggleOption
            lowercase
            className={styles["button-style-toggle-option"]}
            checked={buttonState === BUTTON_STATE_HOVER}
            value={BUTTON_STATE_HOVER}
          >
            Hover
          </ButtonToggleOption>
        </ButtonToggle>
      </div>
      <Separator />

      {customProps.map((prop) => {
        const removeCustomization = () => {
          _onRemoveCustomization(prop.name);
          prop.removeCustomization && prop.removeCustomization();
        };
        return (
          <DynamicInput
            key={prop.name}
            propData={{ ...prop, removeCustomization, disabled: prop?.isLocked }}
            layout={layout}
          />
        );
      })}

      <Box d="flex" justify="center" flex="col" align="center" mb="1" mt="3" pr="1">
        {unCustomizedProps.length > 0 && (
          <Menu shift="left" py="lg" className={styles["view-menu"]}>
            <Button
              iconPrefix="add"
              iconSize="20"
              variant="minimal"
              slot="trigger-element"
              popovertarget="menu"
              disabled={element.get("isLocked")}
            >
              Customize
            </Button>
            {unCustomizedProps.map(({ name, label }) => (
              <MenuItem key={name} onClick={() => handleAddCustomization(name)}>
                {label}
              </MenuItem>
            ))}
          </Menu>
        )}

        {buttonStyleDeviation && (
          <Button
            fullWidth
            variant="link"
            onClick={() => onStyleChange({ detail: { value: buttonStyleBase } })}
            className={`${styles["edit-defaults-button"]} ${styles["reset-to-default-button"]}`}
            disabled={element.get("isLocked")}
          >
            Reset to Defaults
          </Button>
        )}
      </Box>
    </div>
  );
};

export default ButtonProperties;
